import Phaser from "phaser";
import { LEVELS } from "../levels";
import { TILESET_KEY, TILESET_SHEET_KEY, imageLayerKey, imageLayerUrl } from "../tiles";
import { Palette } from "../ui/palette";
import { Tex } from "../ui/textures";
import { loadPortraits } from "../ui/portraits";
import { loadEnemies } from "../ui/enemies";
import { loadRelay } from "../ui/relay";
import { loadIntro } from "../ui/intro";
import { CUTSCENE_PORTRAITS } from "../data/cutscenes";

/** Tiled-authored UI maps (menu screens + the shared HUD), loaded like levels and
 *  scanned for image layers. The HUD is one map reused by every level. */
const UI_MAPS = ["menu-title", "hud"] as const;

/** Maps that back a bespoke scene rather than a LEVELS entry: the Kirby relay's
 *  Tiled scenery (office backgrounds + floor). Loaded + image-scanned like the rest. */
const STANDALONE_MAPS = ["relay"] as const;

/** Loads file-based assets: the shared tileset image + every Tiled map. */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super("Preload");
  }

  preload() {
    const { width, height } = this.scale;
    this.add
      .text(width / 2, height / 2 - 40, "Loading…", { color: Palette.text, fontSize: "20px" })
      .setOrigin(0.5);

    const barW = 320;
    const barX = (width - barW) / 2;
    const barY = height / 2;
    const border = this.add.graphics();
    border.lineStyle(2, 0x3a3f4b, 1).strokeRect(barX - 2, barY - 2, barW + 4, 20);
    const bar = this.add.graphics();
    this.load.on("progress", (p: number) => {
      bar.clear();
      bar.fillStyle(Palette.dan, 1).fillRect(barX, barY, barW * p, 16);
    });

    // Shared tileset image (referenced by every map's embedded tileset).
    this.load.image(TILESET_KEY, "assets/tilesets/office-tiles.png");
    // Same file as a 32px spritesheet, so decor objects (tile objects placed in
    // Tiled) can render an individual tile frame.
    this.load.spritesheet(TILESET_SHEET_KEY, "assets/tilesets/office-tiles.png", {
      frameWidth: 32,
      frameHeight: 32,
    });

    this.load.image("platforms-tiles", "assets/tilesets/platforms-tiles.png");

    // Character atlas (art-pipeline spike): PNG strip + JSON-hash frame map, the
    // same shape real Aseprite/TexturePacker art will ship in. See characterAnim.ts.
    this.load.atlas("char-dan", "assets/characters/dan.png", "assets/characters/dan.json");
    this.load.atlas("char-andrew", "assets/characters/andrew.png", "assets/characters/andrew.json");
    this.load.atlas("char-lucas", "assets/characters/lucas.png", "assets/characters/lucas.json");
    // Lucas's exercise-bike station (the process station). The standalone bike is
    // the idle bike; the mount + pedal clips are 256px-cell spritesheets (kept
    // uniform, NOT union-cropped, so mount's last frame lines up with pedal's
    // first). See src/scenes/Level2LucasScene.ts.
    this.load.image("lucas-bike-standalone", "assets/sprites/lucas-bike-standalone.png");
    this.load.spritesheet("tex-lucas-pedal", "assets/sprites/lucas-pedal-anim.png", { frameWidth: 256, frameHeight: 256 });
    this.load.spritesheet("tex-lucas-mount", "assets/sprites/lucas-bike-mount.png", { frameWidth: 256, frameHeight: 256 });

    // Accessory art (worn upgrades). Placed per animation frame from data authored
    // in tools/accessories.html; see characterAnim.ts accessoryTransform(). The
    // "pickup-" textures are the front-view icons collected in the level.
    this.load.image("acc-headphones", "assets/accessories/headphones.png");
    this.load.image("acc-watch", "assets/accessories/watch.png");
    this.load.image("pickup-headphones", "assets/accessories/headphones-pickup.png");
    this.load.image("pickup-watch", "assets/accessories/watch-pickup.png");

    // Sheepy: 9-frame animated mascot (50x50 per frame). Real art, replaces the
    // old code-drawn rainbow placeholder.
    this.load.spritesheet(Tex.sheepy, "assets/sprites/sheepy.png", { frameWidth: 50, frameHeight: 50 });

    // Dan's throwables: phone (tier 2), email (tier 3), Slack (tier 4).
    this.load.image(Tex.throwPhone, "assets/sprites/phone.png");
    this.load.image(Tex.throwEmail, "assets/sprites/email.png");
    this.load.image(Tex.throwSlack, "assets/sprites/slack.png");
    // Andrew's throwables: code token (tier 1) → Claude token (tier 2).
    this.load.image(Tex.throwCode, "assets/sprites/code.png");
    this.load.image(Tex.throwClaude, "assets/sprites/claude.png");

    // Sci-fi menu chrome: the START button (default + hover swap) and the shared
    // metal frame drawn behind the level-select cards. See widgets.ts / MenuScene.
    this.load.image(Tex.start, "assets/sprites/start.png");
    this.load.image(Tex.startHover, "assets/sprites/start-hover.png");
    this.load.image(Tex.hudBox, "assets/sprites/hud-box.png");
    this.load.image(Tex.hudBoxHover, "assets/sprites/hud-box-hover.png");
    this.load.image(Tex.sheepyHd, "assets/sprites/sheepy-hd.png");
    this.load.image(Tex.sheepyOff, "assets/sprites/sheepy-off.png");
    this.load.image(Tex.lock, "assets/sprites/lock.png");
    // Rating stars: lit + unlit (cards, result screen, level-select counter).
    this.load.image(Tex.star, "assets/sprites/star-on.png");
    this.load.image(Tex.starEmpty, "assets/sprites/star-off.png");
    // Mallorca ending celebration: 13x12 grid of 512x292 frames, 151 filled (rest black).
    this.load.spritesheet(Tex.victory, "assets/sprites/victory.png", { frameWidth: 512, frameHeight: 292 });
    // Market map backdrops (full-screen 16:9), open + hover per region. See MarketSelectScene.
    this.load.image(Tex.regionEurOpen, "assets/images/region-eur-open.png");
    this.load.image(Tex.regionEurHover, "assets/images/region-eur-hover.png");
    this.load.image(Tex.regionUsaOpen, "assets/images/region-usa-open.png");
    this.load.image(Tex.regionUsaHover, "assets/images/region-usa-hover.png");
    this.load.image(Tex.regionUsaWon, "assets/images/region-usa-won.png");
    this.load.image(Tex.gameOver, "assets/images/game-over.png");
    this.load.image(Tex.regionConquered, "assets/sprites/region-conquered.png");
    this.load.spritesheet(Tex.claudeMega, "assets/sprites/claude-coin.png", { frameWidth: 512, frameHeight: 512 });

    // Collectibles: real art replaces the generated shapes (see buildTextures).
    this.load.image(Tex.money, "assets/sprites/money.png");
    this.load.image(Tex.laptop, "assets/sprites/laptop.png");
    this.load.image(Tex.vw, "assets/sprites/vw.png");
    this.load.image(Tex.document, "assets/sprites/document.png");
    this.load.image(Tex.feature, "assets/sprites/feature.png");
    this.load.image(Tex.idea, "assets/sprites/idea.png");
    this.load.image(Tex.bionic, "assets/sprites/bionic.png");

    // Lucas roof rig real art: batteries + key collectibles (real art over the placeholder
    // keys), the coiled cable, and the roof overlays (doors, sign, battery bank, bike cable).
    // Battery: animated (5x4 grid of 256px frames, the fluid crackles). See Level2LucasScene.
    this.load.spritesheet(Tex.battery, "assets/sprites/battery.png", { frameWidth: 256, frameHeight: 256 });
    this.load.image(Tex.key, "assets/sprites/key.png");
    this.load.image(Tex.cable, "assets/sprites/cable.png");
    this.load.image(Tex.bikeCable, "assets/sprites/bike-cable.png");
    this.load.image(Tex.bikeTerminal, "assets/sprites/bike-cable-terminal.png");
    this.load.image(Tex.bionicSign, "assets/sprites/bionic-sign-light.png");
    this.load.image(Tex.closedDoor, "assets/sprites/closed-door.png");
    this.load.image(Tex.garageDoor, "assets/sprites/garage-door.png");
    this.load.image(Tex.openDoorMask, "assets/sprites/open-door-mask.png");
    this.load.image(Tex.dataVaultDoor, "assets/sprites/data-vault-door.png");
    this.load.image(Tex.batteryEmpty, "assets/sprites/battery-empty.png");
    // Installed-battery slot: one-shot power-up (17 frames) then a steady loop (8 frames), both 256px.
    this.load.spritesheet(Tex.batteryInstallStart, "assets/sprites/battery-installed-start.png", { frameWidth: 256, frameHeight: 256 });
    this.load.spritesheet(Tex.batteryInstallLoop, "assets/sprites/battery-installed-loop.png", { frameWidth: 256, frameHeight: 256 });

    // Kirby (finale reactions) + Random PE Guy (Level 1 payoff NPC): packed
    // 128px reaction strips, see src/ui/portraits.ts.
    loadPortraits(this);
    // Kirby's Zoom-style call background (skyline) shown behind his video panel.
    this.load.image("kirby-bg", "assets/sprites/kirby-bg.png");

    // Level 3 enemies: animated bug + beast crawl strips, see src/ui/enemies.ts.
    loadEnemies(this);

    // Andrew's Claude power-up transformation burst (one-shot), see Level3AndrewScene.
    this.load.spritesheet("andrew-powerup", "assets/sprites/andrew-powerup.png", { frameWidth: 256, frameHeight: 237 });

    // Bug / beast deaths: one-shot squash-to-dust played when a bug is stomped.
    this.load.spritesheet("bug-die", "assets/sprites/bug-die.png", { frameWidth: 128, frameHeight: 81 });
    this.load.spritesheet("beast-die", "assets/sprites/beast-die.png", { frameWidth: 128, frameHeight: 93 });

    // Bionic level backdrop: five interchangeable 1536x1024 wall segments, tiled and
    // randomized across the long auto-run level (a single full-width image would blow
    // past the GPU max texture size). See Level3AndrewScene.buildBionicBackground.
    for (let i = 1; i <= 5; i++) this.load.image(`bionic-seg-${i}`, `assets/images/bionic-seg-${i}.png`);

    // Finale relay tag-in / tag-out handoff animations, see src/ui/relay.ts.
    loadRelay(this);

    // Title-screen founders' high-five intro (run-in, connect, loop), see src/ui/intro.ts.
    loadIntro(this);

    // Story-cutscene portraits (Dan / Lucas / Andrew / Kirby), see src/data/cutscenes.ts.
    for (const key of CUTSCENE_PORTRAITS) this.load.image(key, `assets/portraits/${key}.png`);

    // All Tiled maps up front - they're tiny.
    for (const lvl of LEVELS) {
      this.load.tilemapTiledJSON(lvl.mapKey, lvl.mapPath);
    }

    // UI maps authored in Tiled: menu screens (see MenuScene) + the shared HUD,
    // plus standalone-scene maps (the Kirby relay's scenery).
    for (const key of [...UI_MAPS, ...STANDALONE_MAPS]) {
      this.load.tilemapTiledJSON(key, `assets/maps/${key}.tmj`);
    }
  }

  create() {
    // Second pass: the maps are now parsed, so we can see which image layers they
    // reference and pull those images in before starting the game. (Phaser does
    // not auto-load image-layer images.) If none, start immediately.
    const mapKeys = [...LEVELS.map((l) => l.mapKey), ...UI_MAPS, ...STANDALONE_MAPS];
    const wanted = new Map<string, string>();
    for (const key of mapKeys) {
      const raw = this.cache.tilemap.get(key)?.data as { layers?: Array<Record<string, unknown>> } | undefined;
      for (const layer of raw?.layers ?? []) {
        if (layer.type === "imagelayer" && typeof layer.image === "string") {
          wanted.set(imageLayerKey(layer.image), imageLayerUrl(layer.image));
        }
      }
    }

    const missing = [...wanted].filter(([key]) => !this.textures.exists(key));
    if (missing.length === 0) {
      this.bootGame();
      return;
    }

    for (const [key, url] of missing) this.load.image(key, url);
    // COMPLETE fires even if some images 404; a missing texture is simply skipped
    // when the level renders, so a bad path never hangs the loader.
    this.load.once(Phaser.Loader.Events.COMPLETE, () => this.bootGame());
    this.load.start();
  }

  /** Boot flow: the intro story cutscene -> Title -> MarketSelect -> LevelSelect. */
  private bootGame() {
    this.scene.start("Dialogue", { scriptKey: "intro", next: "Title" });
  }
}
